// CreditBalance — compact credit display with low-balance warning
// Shows current credits and subscription status in a small inline widget.

import { useCreditBalance } from "../../hooks/useCredits";
import { Badge } from "../ui/Badge";

interface CreditBalanceProps {
  /** Show as compact inline (header) or expanded (page) */
  variant?: "compact" | "expanded";
  className?: string;
}

export function CreditBalance({
  variant = "compact",
  className,
}: CreditBalanceProps) {
  const { data, isLoading, isError } = useCreditBalance();
  const balance = data?.data;

  if (isLoading) {
    return (
      <span className={className}>
        <span className="inline-block h-4 w-10 animate-pulse rounded bg-slate-200" />
      </span>
    );
  }

  if (isError || !balance) {
    return (
      <span className={className}>
        <Badge variant="error">--</Badge>
      </span>
    );
  }

  const { credits, subscription_status, low_balance } = balance;

  if (variant === "compact") {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ""}`}>
        <span
          className={`text-sm font-semibold tabular-nums ${
            low_balance ? "text-amber-600" : "text-slate-700"
          }`}
        >
          {credits}
        </span>
        <span className="text-xs text-slate-400">credits</span>
        {low_balance && (
          <Badge variant="warning">Low</Badge>
        )}
      </span>
    );
  }

  // Expanded variant — shows more detail
  const statusBadge: Record<string, { label: string; variant: "success" | "warning" | "error" | "default" }> = {
    active: { label: "Active", variant: "success" },
    past_due: { label: "Past Due", variant: "warning" },
    cancelled: { label: "Cancelled", variant: "error" },
    free: { label: "Free Tier", variant: "default" },
  };

  const status = statusBadge[subscription_status] ?? statusBadge.free!;

  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-4 ${className ?? ""}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">Available Credits</p>
          <p
            className={`text-3xl font-bold tabular-nums ${
              low_balance ? "text-amber-600" : "text-slate-900"
            }`}
          >
            {credits}
          </p>
        </div>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>
      {low_balance && (
        <p className="mt-2 text-sm text-amber-600">
          Your credit balance is low. Top up to continue creating content.
        </p>
      )}
    </div>
  );
}
